const STORAGE_KEY = 'advoq_chat_journey_v1';
const MAX_ROUTE_ENTRIES = 12;
const MAX_MESSAGE_ENTRIES = 24;

function cleanText(value, max = 240) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function safeParse(raw) {
  if (!raw) return {};
  try {
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function readStore() {
  if (typeof window === 'undefined') return {};
  return safeParse(window.localStorage.getItem(STORAGE_KEY));
}

function writeStore(next) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next || {}));
  } catch {}
}

export function buildChatContinuityKey({
  workspaceId = '',
  chatId = '',
  matterId = '',
  linkedDocId = '',
} = {}) {
  const pieces = [
    cleanText(workspaceId, 80),
    cleanText(chatId, 80),
    cleanText(matterId, 80),
    cleanText(linkedDocId, 80),
  ].filter(Boolean);
  return pieces.join('::') || 'chat::new';
}

export function readChatJourney(key) {
  if (!key) return null;
  const store = readStore();
  return store[key] || null;
}

export function recordJourneyVisit(key, visit) {
  if (!key || !visit?.href) return null;
  const store = readStore();
  const existing = store[key] || {};
  const routes = Array.isArray(existing.routes) ? existing.routes : [];
  const nextVisit = {
    surface: cleanText(visit.surface, 40) || 'CHAT',
    label: cleanText(visit.label, 80) || 'Workspace',
      href: cleanText(visit.href, 600),
      visited_at: new Date().toISOString(),
  };
  const filtered = routes.filter(
    (item) => item?.href && item.href !== nextVisit.href && item.surface !== nextVisit.surface,
  );
  const next = {
    ...existing,
    routes: [nextVisit, ...filtered].slice(0, MAX_ROUTE_ENTRIES),
  };
  store[key] = next;
  writeStore(store);
  return next;
}

export function listJourneyRoutes(key) {
  const routes = readChatJourney(key)?.routes;
  return Array.isArray(routes) ? routes : [];
}

function buildMessageUiState(messages) {
  const next = {};
  (messages || []).forEach((message) => {
    if (message?.role !== 'assistant' || !message?.message_id) return;
    const refinement = message.response_refinement || {};
    const draft = String(refinement.draft || '').trim();
    const feedback = String(refinement.feedback || '').trim();
    if (!refinement.open && !draft && !feedback) return;
    next[message.message_id] = {
      open: Boolean(refinement.open),
      feedback: refinement.feedback || '',
      draft: refinement.draft || '',
      answer_goal: refinement.answer_goal || '',
      missing_facts_to_flag: Array.isArray(refinement.missing_facts_to_flag)
        ? refinement.missing_facts_to_flag.slice(0, 6)
        : [],
    };
  });
  return Object.fromEntries(Object.entries(next).slice(0, MAX_MESSAGE_ENTRIES));
}

export function saveChatUiSnapshot(key, { composerImprovement = null, messages = [] } = {}) {
  if (!key) return null;
  const store = readStore();
  const existing = store[key] || {};
  const uiState = {
    ...(existing.ui_state || {}),
  };
  const next = {
    ...existing,
    ui_state: {
      ...uiState,
      composer_improvement: composerImprovement && String(composerImprovement.draft || '').trim()
        ? {
            open: Boolean(composerImprovement.open),
            draft: composerImprovement.draft || '',
            answer_goal: composerImprovement.answer_goal || '',
            missing_facts_to_flag: Array.isArray(composerImprovement.missing_facts_to_flag)
              ? composerImprovement.missing_facts_to_flag.slice(0, 6)
              : [],
          }
        : null,
      message_states: buildMessageUiState(messages),
      saved_at: new Date().toISOString(),
    },
  };
  store[key] = next;
  writeStore(store);
  return next.ui_state;
}

export function loadChatUiSnapshot(key) {
  const uiState = readChatJourney(key)?.ui_state;
  return uiState && typeof uiState === 'object' ? uiState : null;
}

export function updateChatUiSnapshotMeta(key, meta = {}) {
  if (!key || !meta || typeof meta !== 'object') return null;
  const store = readStore();
  const existing = store[key] || {};
  const nextUiState = {
    ...(existing.ui_state || {}),
    ...(typeof meta.scroll_top === 'number' ? { scroll_top: Math.max(0, Math.round(meta.scroll_top)) } : {}),
    ...(typeof meta.show_right_panel === 'boolean' ? { show_right_panel: meta.show_right_panel } : {}),
    ...(meta.right_panel_tab ? { right_panel_tab: cleanText(meta.right_panel_tab, 32) } : {}),
    saved_at: new Date().toISOString(),
  };
  store[key] = {
    ...existing,
    ui_state: nextUiState,
  };
  writeStore(store);
  return nextUiState;
}

export function restoreChatMessagesWithUiState(messages, uiState) {
  const messageStates = uiState?.message_states || {};
  if (!Array.isArray(messages) || !messageStates || typeof messageStates !== 'object') return Array.isArray(messages) ? messages : [];
  return messages.map((message) => {
    if (message?.role !== 'assistant' || !message?.message_id) return message;
    const saved = messageStates[message.message_id];
    if (!saved) return message;
    return {
      ...message,
      response_refinement: {
        open: Boolean(saved.open),
        feedback: saved.feedback || '',
        draft: saved.draft || '',
        answer_goal: saved.answer_goal || '',
        missing_facts_to_flag: Array.isArray(saved.missing_facts_to_flag) ? saved.missing_facts_to_flag : [],
      },
    };
  });
}
